import { PhFileText, PhSquaresFour } from 'ember-phosphor-icons';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';

// kind-config — the single source of truth for the differences between the
// Reports Home and the Dashboards Home. The reports/* components (home-page,
// browse-page, browse-rail, report-row, report-actions[-menu], pinned-card,
// product-icon-badge) render identically for both; everything that actually
// differs between a "report" and a "dashboard" lives here and is selected by a
// `@kind` arg (default 'report', so the reports surface is unchanged).
// The load-bearing difference is link construction: reports open via a query
// param on the builder/viewer routes (`?reportId=`), while dashboards are the
// same resource addressed positionally on their own routes
// (authenticated.dashboards.edit/.view with `:dashboard_id`). Each link builder
// therefore returns a discriminated shape the templates branch on.

export type ReportKind = 'report' | 'dashboard';

/**
 * A resolved LinkTo target. Exactly one of `query` / `models` is set:
 * - reports use `{ route, query: { reportId } }`
 * - dashboards use `{ route, models: [id] }`
 * Templates branch on `models` presence.
 */
export interface KindLink {
  route: string;
  query?: Record<string, string>;
  models?: string[];
}

export interface KindConfig {
  kind: ReportKind;
  /** 'report' / 'dashboard' — lower-case noun for inline copy. */
  nounSingular: string;
  /** 'reports' / 'dashboards'. */
  nounPlural: string;
  /** Page title, e.g. "Reports" / "Dashboards". */
  title: string;
  /** Primary CTA label, e.g. "New Report" / "New Dashboard". */
  newLabel: string;
  /** Search input placeholder. */
  searchPlaceholder: string;
  /** Reports group by product; dashboards do not. */
  productGrouping: boolean;
  /** DI service names (separate singletons keep catalog, pins and folders independent). */
  catalogServiceName: string;
  preferencesServiceName: string;
  foldersServiceName: string;
  /** Route names for this kind's own home / browse surfaces. */
  homeRoute: string;
  browseRoute: string;
  /** Optional cross-link rendered in the header (Reports ⇄ Dashboards). */
  crossLink?: { route: string; label: string };
  /** Open (edit/run) an existing item — the row/card name link. */
  linkToOpen: (id: string) => KindLink;
  /** Start a new item. */
  linkToNew: () => KindLink;
  /** Read-only view of an existing item. */
  linkToView: (id: string) => KindLink;
  /** Icon fallback when product grouping is off (dashboards). */
  fallbackIcon: typeof PhFileText;
}

export const REPORT_KIND: KindConfig = {
  kind: 'report',
  nounSingular: 'report',
  nounPlural: 'reports',
  title: 'Reports',
  newLabel: 'New Report',
  searchPlaceholder: 'Search reports by name, product, or company…',
  productGrouping: true,
  catalogServiceName: 'reports-catalog',
  preferencesServiceName: 'reports-preferences',
  foldersServiceName: 'report-folders',
  homeRoute: 'authenticated.reports.index',
  browseRoute: 'authenticated.reports.browse',
  crossLink: { route: 'authenticated.dashboards.index', label: 'Dashboards' },
  linkToOpen: (id) => ({ route: 'authenticated.reports.builder', query: { reportId: id } }),
  linkToNew: () => ({ route: 'authenticated.reports.builder', query: { reportId: '' } }),
  linkToView: (id) => ({ route: 'authenticated.reports.viewer', query: { reportId: id } }),
  fallbackIcon: PhFileText,
};

export const DASHBOARD_KIND: KindConfig = {
  kind: 'dashboard',
  nounSingular: 'dashboard',
  nounPlural: 'dashboards',
  title: 'Dashboards',
  newLabel: 'New Dashboard',
  searchPlaceholder: 'Search dashboards by name or company…',
  productGrouping: false,
  catalogServiceName: 'dashboards-catalog',
  preferencesServiceName: 'dashboards-preferences',
  foldersServiceName: 'dashboard-folders',
  homeRoute: 'authenticated.dashboards.index',
  browseRoute: 'authenticated.dashboards.browse',
  crossLink: { route: 'authenticated.reports.index', label: 'Reports' },
  linkToOpen: (id) => ({ route: 'authenticated.dashboards.edit', models: [id] }),
  linkToNew: () => ({ route: 'authenticated.dashboards.builder' }),
  linkToView: (id) => ({ route: 'authenticated.dashboards.view', models: [id] }),
  fallbackIcon: PhSquaresFour,
};

const KIND_CONFIGS: Record<ReportKind, KindConfig> = {
  report: REPORT_KIND,
  dashboard: DASHBOARD_KIND,
};

/** Resolve the config for a kind (defaults to 'report'). */
export function configFor(kind: ReportKind | undefined): KindConfig {
  return KIND_CONFIGS[kind ?? 'report'];
}

/** Search-hint copy, kept in one place so both kinds read consistently. */
export function searchHint(
  config: KindConfig,
  {
    total,
    matches,
    isSearching,
    mayHaveMore,
  }: {
    total: number;
    matches: number;
    isSearching: boolean;
    mayHaveMore: boolean;
  }
): string {
  if (isSearching) return `${matches} matches across ${total} ${config.nounPlural}`;
  const scope = ' — global, custom partner, and yours';
  if (mayHaveMore) {
    return `Searches the ${total} most recently updated ${config.nounPlural}${scope}`;
  }
  return `Searches all ${total} ${config.nounPlural}${scope}`;
}

export type { Report };
